// client/door/FaceControl.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import type { VerifyResp } from "./types";
import { twa, haptic, confirmPopup } from "./utils/twa";
import { extractToken } from "./utils/extractToken";

import FullscreenScanner from "./components/FullscreenScanner";
import SuccessScreen from "./components/SuccessScreen";
import { normalizeRole, homeRouteFor } from "../lib/rbac";

// Единый роутер + API
import { ROUTE, API } from "../lib/paths";
import { api } from "../lib/api";

type View = "home" | "scan" | "admit" | "success";

type Stats = {
  entries_today: number;
  denied_today: number;
  waiting_now: number;
};

export default function FaceControl() {
  const tg = twa();

  const [view, setView] = useState<View>("home");
  const [token, setToken] = useState<string>("");
  const [verify, setVerify] = useState<VerifyResp | null>(null);
  const [successNote, setSuccessNote] = useState<string>("");

  const [stats, setStats] = useState<Stats>({
    entries_today: 0,
    denied_today: 0,
    waiting_now: 0,
  });

  // отмена «поздних» ответов verify
  const tokenRef = useRef<string>("");
  const verifyAbortRef = useRef<AbortController | null>(null);
  const lastScanRef = useRef<{ token: string; ts: number } | null>(null);
  const lastVerifyRef = useRef<{ token: string; ts: number } | null>(null);
  const viewRef = useRef<View>("home");

  // блокируем кнопки во время операций
  const [opRunning, setOpRunning] = useState(false);

  useEffect(() => {
    tg?.ready();
    tg?.expand();

    applyRoute(ROUTE.parse());
    void refreshStats();

    const onPop = () => applyRoute(ROUTE.parse());
    window.addEventListener("popstate", onPop);

    // Подписка на WS, чтобы ловить отметки от других устройств
    const wsUrl =
      (location.protocol === "https:" ? "wss://" : "ws://") +
      location.host +
      (import.meta.env.VITE_WS_PATH || "/ws");
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (!m || !verify) return;

          // если меня касается — обновляем UI
          if (m.type === "checkin" && m.payload?.user_id === verify?.user?.id) {
            // локально помечаем, чтобы UI сразу стал «использован»
            setVerify((v) =>
              v ? { ...v, checked_in: true, reason: "already_checked_in" as any } : v
            );
            setSuccessNote("Гость отмечен как вошедший");
            setView("success");
          }
          if (m.type === "deny" && m.payload?.user_id === verify?.user?.id) {
            setVerify((v) =>
              v ? { ...v, paid: false, reason: "denied" as any } : v
            );
          }
        } catch {}
      };
    } catch {}

    return () => {
      window.removeEventListener("popstate", onPop);
      try { verifyAbortRef.current?.abort(); } catch {}
      verifyAbortRef.current = null;
      try { ws?.close(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  function applyRoute(r: ReturnType<typeof ROUTE.parse>) {
    try { verifyAbortRef.current?.abort(); } catch {}
    verifyAbortRef.current = null;

    if (ROUTE.isDoorHome(r)) {
      tokenRef.current = "";
      setView("home");
      setToken("");
      setVerify(null);
      return;
    }
    if (ROUTE.isDoorScan(r)) {
      tokenRef.current = "";
      setView("scan");
      setToken("");
      setVerify(null);
      return;
    }
    if (ROUTE.isDoorToken(r)) {
      const t = extractToken(r.token) || r.token;
      tokenRef.current = t;
      setToken(t);
      setView("admit");
      // если токен поменялся — верифицируем
      if (!verify || verify?.ok === undefined || t !== token) {
        void doVerify(t);
      }
      return;
    }
  }

  async function refreshStats() {
    try {
      const r = await fetch(API.doorStats(), { credentials: "include" });
      if (r.ok) {
        const s = await r.json();
        setStats({
          entries_today: s?.entries_today ?? 0,
          denied_today: s?.denied_today ?? 0,
          waiting_now: s?.waiting_now ?? 0,
        });
      }
    } catch {}
  }

  // --------- переходы ----------
  async function goHome() {
    tokenRef.current = "";
    try {
      const me = await api.me();
      const role = normalizeRole(me.role);
      ROUTE.go(homeRouteFor(role));
    } catch {
      ROUTE.go(homeRouteFor(null));
    }
  }
  function goScan() {
    try { verifyAbortRef.current?.abort(); } catch {}
    verifyAbortRef.current = null;

    tokenRef.current = "";
    setToken("");
    setVerify(null);
    setView("scan");

    ROUTE.go(ROUTE.scan(), true);
  }
  function goAdmit(v: VerifyResp, resolvedToken: string) {
    tokenRef.current = resolvedToken;
    ROUTE.go(ROUTE.token(resolvedToken), true);
    setVerify(v);
    setToken(resolvedToken);
    setView("admit");
  }

  // --------- скан ----------
  function handleScanDetected(qrText: string) {
    if (viewRef.current !== "scan") return;
    const t = extractToken(qrText);
    if (!t) {
      haptic("warning");
      return;
    }
    // While verify is in-flight or operation running, ignore new scans
    if (verifyAbortRef.current || opRunning) {
      return;
    }
    // iOS: камера может дублировать одно и то же распознавание — гасим повторные вибрации
    const now = Date.now();
    const last = lastScanRef.current;
    if (last && last.token === t && (now - last.ts) < 8000) {
      return;
    }
    lastScanRef.current = { token: t, ts: now };
    haptic("success");
    void doVerify(t);
  }

  async function doVerify(t: string) {
    tokenRef.current = t;
    const last = lastVerifyRef.current;
    if (last && last.token === t && (Date.now() - last.ts) < 10000) {
      return;
    }
    lastVerifyRef.current = { token: t, ts: Date.now() };

    try { verifyAbortRef.current?.abort(); } catch {}
    const ctrl = new AbortController();
    verifyAbortRef.current = ctrl;

    try {
      const r = await fetch(API.entryVerify(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token: t }),
        signal: ctrl.signal,
      });

      const data: VerifyResp = await r.json();

      // ответ не про актуальный токен — игнор
      if (tokenRef.current !== t) return;

      goAdmit(data, t);
      // не дублируем вибрацию для того же токена подряд
      const last = lastScanRef.current;
      const recentSame = last && last.token === t && (Date.now() - last.ts) < 1200;
      if (!recentSame) {
        if (!data.ok || !data.paid || data.checked_in) haptic("error");
        else haptic("success");
      }
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      if (tokenRef.current !== t) return;
      goAdmit({ ok: false, reason: "Сеть недоступна" } as VerifyResp, t);
      haptic("error");
    } finally {
      if (verifyAbortRef.current === ctrl) verifyAbortRef.current = null;
    }
  }

  // --------- действия двери ----------
  async function onAllow() {
    if (!token || opRunning) return;
    setOpRunning(true);

    // гарантированно останавливаем любые verify-запросы,
    // чтобы поздний ответ не перерисовал карточку
    try { verifyAbortRef.current?.abort(); } catch {}
    verifyAbortRef.current = null;

    try {
      const r = await fetch(API.entryCheckin(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token }),
      });
      if (!r.ok) throw new Error("checkin_failed");

      // оптимистично помечаем как использованный и показываем успех
      setVerify((v) =>
        v ? { ...v, checked_in: true, reason: "already_checked_in" as any } : v
      );
      haptic("impact");
      setSuccessNote("Гость отмечен как вошедший");
      setView("success");
    } catch {
      // возвращаем в режим admit, но НЕ запускаем verify повторно
      setVerify((v) => v ? { ...v, ok: false, reason: "Ошибка сервера (checkin)" as any } : v);
      haptic("error");
    } finally {
      setOpRunning(false);
    }
  }

  async function onDeny() {
    if (!token || opRunning) return;
    const ok = await confirmPopup(
      "Подтвердите отказ",
      "Действие необратимо: гость не будет отмечен как вошедший.",
    );
    if (!ok) return;

    setOpRunning(true);
    try {
      try { verifyAbortRef.current?.abort(); } catch {}
      verifyAbortRef.current = null;

      const r = await fetch(API.entryDeny(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, note: "event_staff_miniapp" }),
      });
      if (!r.ok) throw new Error("deny_failed");

      haptic("warning");
      // после отказа — сразу к сканеру
      goScan();
    } catch {
      setVerify((v) => v ? { ...v, ok: false, reason: "Ошибка сервера (deny)" as any } : v);
      haptic("error");
    } finally {
      setOpRunning(false);
    }
  }

  // --------- вычисления для UI ----------
  const admitView = useMemo(() => {
    const ok = !!verify?.ok;
    const paid = !!verify?.paid;
    const used = !!verify?.checked_in;
    const username = verify?.user?.username ? `@${verify?.user?.username}` : "";
    const name = verify?.user?.name || (verify as any)?.user?.first_name || "";
    const group =
      (verify as any)?.user?.group_name ??
      (verify as any)?.user?.group ??
      (verify as any)?.user?.role ??
      null;

    // новые поля из ответа бэка
    const eventTitle = (verify as any)?.event?.title ?? "—";
    const ticketLabel = (verify as any)?.ticket?.label ?? "—";

    const badge = !ok
      ? { text: "Ошибка", cls: "bg-red-600" }
      : !paid
      ? { text: "Не оплачено", cls: "bg-red-600" }
      : used
      ? { text: "Уже входил", cls: "bg-amber-500" }
      : { text: "Оплата подтверждена", cls: "bg-emerald-600" };

    return { ok, paid, used, name, username, group, eventTitle, ticketLabel, badge };
  }, [verify]);

  // --------- UI ----------
  return (
    <div className="relative min-h-dvh p-5 text-[var(--text)]">

      {view === "home" && (
        <div className="relative z-10 mx-auto max-w-[720px]">
          <div className="ui-card p-6 sm:p-8">
            <header className="flex items-start justify-between gap-3">
              <div>
                <h1 className="text-[32px] leading-[1.05] font-extrabold tracking-tight">
                  Вход · <span className="text-white/90">Face control</span>
                </h1>
                <p className="mt-2 text-sm text-neutral-300">
                  Сканируйте QR гостя и подтвердите вход.
                </p>
              </div>
              <span className="ui-chip shrink-0">
                v2.1
              </span>
            </header>

            <ol className="mt-5 grid gap-2 text-[13px] text-neutral-300">
              <li>1) Нажмите «Сканировать QR» и наведите камеру на пропуск гостя.</li>
              <li>2) Проверьте статус оплаты и нажмите «Впустить» или «Отказать».</li>
              <li>3) После отметки входа экран переключится на «Впущен».</li>
            </ol>

            <button
              onClick={goScan}
              className="btn-primary mt-6 w-full text-lg"
            >
              Сканировать QR
            </button>

            <div className="mt-6">
              <StatsPanel stats={stats} onRefresh={refreshStats} />
            </div>
          </div>
        </div>
      )}

      {view === "scan" && (
        <FullscreenScanner onClose={goHome} onDetected={handleScanDetected} />
      )}

      {view === "admit" && (
        <div className="relative z-10 mx-auto max-w-[720px]">
          <AdmissionCard
            token={token}
            verify={verify}
            ui={admitView}
            onBack={goHome}
            onAllow={onAllow}
            onDeny={onDeny}
            onScanNext={goScan}
            disabled={opRunning}
          />
        </div>
      )}

      {view === "success" && (
        <SuccessScreen
          title="Впущен"
          caption={successNote || "Гость отмечен как вошедший"}
          onPrimary={goScan}
          onSecondary={goHome}
        />
      )}
    </div>
  );
}

function StatsPanel({
  stats,
  onRefresh,
}: {
  stats: Stats;
  onRefresh: () => void;
}) {
  return (
    <div className="ui-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-neutral-300">Статистика смены</div>
        <button
          onClick={onRefresh}
          className="rounded-lg border border-white/15 bg-white/10 px-3 py-1 text-xs text-white hover:bg-white/15"
        >
          Обновить
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Впущено" value={stats.entries_today} />
        <StatTile label="Отказы" value={stats.denied_today} />
        <StatTile label="В очереди" value={stats.waiting_now} />
      </div>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-center">
      <div className="text-xs text-neutral-400">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function AdmissionCard({
  token,
  verify,
  ui,
  onBack,
  onAllow,
  onDeny,
  onScanNext,
  disabled = false,
}: {
  token: string;
  verify: VerifyResp | null;
  ui: {
    ok: boolean;
    paid: boolean;
    used: boolean;
    name: string;
    username: string;
    group: string | null;
    eventTitle: string;      // NEW
    ticketLabel: string;     // NEW
    badge: { text: string; cls: string };
  };
  onBack: () => void;
  onAllow: () => void;
  onDeny: () => void;
  onScanNext?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.35)] p-6 sm:p-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-[24px] font-bold">Проверка доступа</h2>
            <span className={`rounded-md px-2 py-1 text-xs text-white ${ui.badge.cls}`}>
              {ui.badge.text}
            </span>
          </div>
          <div className="mt-1 break-all text-xs text-neutral-400">Токен: {token}</div>
        </div>
      </div>

      <div className="mt-5 grid gap-2 rounded-2xl border border-white/10 bg-white/5 p-4">
        <Row
          inline
          label="Гость"
          value={
            <>
              <span className="font-semibold">{ui.name || "—"}</span>
              {ui.username && <span className="text-neutral-400"> ({ui.username})</span>}
            </>
          }
        />
        {ui.group && <Row inline label="Группа" value={ui.group || "—"} />}
        <Row
          inline
          label="Оплата"
          value={
            ui.paid ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-600/90 px-2 py-0.5 text-xs text-white">
                Оплачено
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-md bg-red-600/90 px-2 py-0.5 text-xs text-white">
                Не оплачено
              </span>
            )
          }
        />
        {/* NEW: выводим событие и тариф */}
        <Row inline label="Мероприятие" value={ui.eventTitle} />
        <Row inline label="Билет" value={ui.ticketLabel} />

        {ui.used && (
          <div className="mt-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            Внимание: этот пропуск уже использован.
          </div>
        )}
        {/* Убрали «Код сверки» полностью */}
        {verify?.reason && !ui.ok && (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            {verify.reason}
          </div>
        )}
      </div>

      {!ui.used && (
        <div className="mt-6 grid grid-cols-2 gap-4">
          <button
            onClick={onDeny}
            disabled={disabled}
            className="group relative w-full rounded-2xl px-4 py-4 font-semibold text-white border border-white/12 bg-white/[.06] shadow-[0_8px_24px_rgba(0,0,0,.28)] hover:bg-white/[.10] active:translate-y-[1px] transition after:content-[''] after:absolute after:left-3 after:right-3 after:bottom-2 after:h-[3px] after:rounded-full after:bg-red-500/70 group-hover:after:bg-red-400/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/50 disabled:opacity-55 disabled:cursor-not-allowed"
          >
            Отказать
          </button>

          <button
            onClick={onAllow}
            disabled={disabled || !ui.ok || !ui.paid || ui.used}
            className="group relative w-full rounded-2xl px-4 py-4 font-semibold text-white border border-white/12 bg-white/[.06] shadow-[0_8px_24px_rgba(0,0,0,.28)] hover:bg-white/[.10] active:translate-y-[1px] transition after:content-[''] after:absolute after:left-3 after:right-3 after:bottom-2 after:h-[3px] after:rounded-full after:bg-emerald-500/70 group-hover:after:bg-emerald-400/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/50 disabled:opacity-55 disabled:cursor-not-allowed"
          >
            Впустить
          </button>
        </div>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <button
          onClick={onBack}
          className="shrink-0 rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-xs text-white hover:bg-white/15"
        >
          На главную
        </button>
        <button
          onClick={() => {
            onScanNext?.();
          }}
          className="w-full rounded-xl border border-white/10 bg-white/5 py-3 text-sm text-white hover:bg-white/10"
        >
          Сканировать следующего
        </button>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  inline = false,
}: {
  label: string;
  value: React.ReactNode;
  inline?: boolean;
}) {
  if (inline) {
    return (
      <div className="text-sm whitespace-nowrap overflow-hidden text-ellipsis">
        <span className="text-sm text-neutral-400 align-baseline">{label}: </span>
        <span className="align-baseline">{value}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-base text-neutral-400">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
